import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectCurrentUser, selectCurrentCompany } from "../../store/slices/authSlice";
import { useJourneyProgress } from "../../hooks/useJourneyProgress";
import { useGetMyTeamMembersQuery, useGetMyCompanyCompetencySetQuery } from "../../services/api";
import "./Dashboard.css";

export default function Dashboard() {
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const company = useSelector(selectCurrentCompany);
  const companyId = company?._id || company?.id;

  // Journey progress for unlock logic
  const {
    step2Unlocked,     // Diagnostics unlocked (values complete)
    step3Unlocked,     // Competencies unlocked (diagnostics complete)
    valuesComplete,
    diagnosticsComplete,
    isLoading: progressLoading,
  } = useJourneyProgress({ forceFetch: true });

  // Stats data
  const { data: membersData } = useGetMyTeamMembersQuery(
    { companyId },
    { skip: !companyId }
  );
  const { data: competencyData } = useGetMyCompanyCompetencySetQuery(undefined, {
    skip: !companyId,
  });

  // Calculate stats
  const employeeCount = membersData?.members?.length || 0;
  const competencyCount = competencyData?.values?.reduce(
    (sum, value) => sum + (value.competencies?.length || 0),
    0
  ) || 0;

  // Calculate steps progress (4 steps total in journey)
  const completedSteps = [
    valuesComplete,
    diagnosticsComplete,
    step3Unlocked,
    step3Unlocked, // Step 4 same as step 3
  ].filter(Boolean).length;

  // Get username
  const userName = user ? `${user.firstName} ${user.lastName}` : "User";

  return (
    <>
      {/* Welcome */}
      <div className="welcome">Welcome Back,</div>
      <h1 className="username">{userName}</h1>

      {/* Hero */}
      <section className="hero">
        <div className="dashboard-hero-inner">
          <h2>Transform values into action</h2>
          <p>
            Turn your organization's values into clear, practical competencies
            that guide hiring, promotion, and staff engagement and development.
          </p>
        </div>
        <div className="hero-buttons">
          <button
            className="btn primary"
            onClick={() => navigate('/values')}
          >
            Start Unpacking Values →
          </button>
          <button
            className={`btn primary ${!step3Unlocked ? 'disabled' : ''}`}
            onClick={() => step3Unlocked && navigate('/competencies-list')}
            disabled={!step3Unlocked}
          >
            View Competency List →
          </button>
          <button
            className={`btn ghost ${!step3Unlocked ? 'disabled' : ''}`}
            onClick={() => step3Unlocked && navigate('/competencies')}
            disabled={!step3Unlocked}
          >
            View Rubrics →
          </button>
        </div>
      </section>

      {/* ===== Stats ===== */}
      <section className="stats">
        <div className="stat">
          <div className="stat-head">
            <span className="stat-icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
              <g clip-path="url(#clip0_1_2684)">
                <path d="M3.33334 4.66667C3.33334 5.37391 3.61429 6.05219 4.11439 6.55229C4.61449 7.05238 5.29277 7.33333 6.00001 7.33333C6.70725 7.33333 7.38553 7.05238 7.88563 6.55229C8.38573 6.05219 8.66668 5.37391 8.66668 4.66667C8.66668 3.95942 8.38573 3.28115 7.88563 2.78105C7.38553 2.28095 6.70725 2 6.00001 2C5.29277 2 4.61449 2.28095 4.11439 2.78105C3.61429 3.28115 3.33334 3.95942 3.33334 4.66667Z" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M2 14V12.6667C2 11.9594 2.28095 11.2811 2.78105 10.781C3.28115 10.281 3.95942 10 4.66667 10H7.33333C8.04058 10 8.71885 10.281 9.21895 10.781C9.71905 11.2811 10 11.9594 10 12.6667V14" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M10.6667 2.08667C11.2403 2.23354 11.7487 2.56714 12.1117 3.03488C12.4748 3.50262 12.6719 4.07789 12.6719 4.67C12.6719 5.26212 12.4748 5.83739 12.1117 6.30513C11.7487 6.77287 11.2403 7.10647 10.6667 7.25334" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14 14.0001V12.6668C13.9966 12.0782 13.7986 11.5073 13.4368 11.043C13.0751 10.5788 12.5699 10.2472 12 10.1001" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_1_2684">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg></span>
            <span className="stat-title">Total Employee</span>
          </div>
          <div className="stat-value">{String(employeeCount).padStart(2, '0')}</div>
        </div>

        <div className="stat">
          <div className="stat-head">
            <span className="stat-icon" ><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
              <g clip-path="url(#clip0_1_2696)">
                <path d="M5.70668 2.45996C4.97873 2.76129 4.31726 3.20302 3.76001 3.75996" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M2.46 5.70679C2.1577 6.43365 2.0014 7.2129 2 8.00012" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M2.46002 10.2932C2.76135 11.0212 3.20309 11.6826 3.76002 12.2399" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M5.70667 13.54C6.43353 13.8423 7.21278 13.9986 8 14" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M10.2933 13.54C11.0213 13.2387 11.6828 12.7969 12.24 12.24" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M13.54 10.2933C13.8423 9.56647 13.9986 8.78722 14 8" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M13.54 5.70668C13.2387 4.97873 12.7969 4.31726 12.24 3.76001" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M10.2933 2.46C9.56647 2.1577 8.78722 2.0014 8 2" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_1_2696">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg></span>
            <span className="stat-title">Competencies</span>
          </div>
          <div className="stat-value">{String(competencyCount).padStart(2, '0')}</div>
        </div>

        <div className="stat">
          <div className="stat-head">
            <span className="stat-icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
              <g clip-path="url(#clip0_1_2712)">
                <path d="M3.33337 8.00008L6.66671 11.3334L13.3334 4.66675" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_1_2712">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg></span>
            <span className="stat-title">Completed Rubrics</span>
          </div>
          <div className="stat-value">{String(diagnosticsComplete ? 1 : 0).padStart(2, '0')}</div>
        </div>

        <div className="stat">
          <div className="stat-head">
            <span className="stat-icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
              <g clip-path="url(#clip0_1_2721)">
                <path d="M2 11.3334L6 7.33341L8.66667 10.0001L14 4.66675" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M9.33337 4.66675H14V9.33341" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_1_2721">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg></span>
            <span className="stat-title">Steps</span>
          </div>
          <div className="stat-value">{completedSteps} of 4</div>
        </div>
      </section>

      {/* ===== Process ===== */}
      <section className="process">
        <div className="process-header">
          <h3>Legacy72 Learning Cycle Process</h3>
          <p className="muted">
            Transform your organization's values into clear expectations for leadership success!
          </p>
        </div>

        <ul className="steps">
          <li className="step row">
            <span className="badge">1</span>
            <div className="step-text">
              <div className="step-title">Unpack Values</div>
              <div className="step-desc">
                Collect concrete examples of how values show up in everyday behavior
              </div>
            </div>
          </li>

          <li className="step row">
            <span className="badge">2</span>
            <div className="step-text">
              <div className="step-title">Build Competency Rubric</div>
              <div className="step-desc">
                Turn examples into a clear, leveled grid showing what “good” looks like
              </div>
            </div>
          </li>

          <li className="step row">
            <span className="badge">3</span>
            <div className="step-text">
              <div className="step-title">Self and Peer Diagnostics</div>
              <div className="step-desc">
                Assess your alignment with competencies and identify strengths and growth opportunities              </div>
            </div>
          </li>
        </ul>
      </section>

      <section className="actions">
        <div className="action-card">
          <div className="action-head">
            <span className="action-icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
              <g clip-path="url(#clip0_1_2640)">
                <path d="M8 3.33325V12.6666" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M3.33334 8H12.6667" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_1_2640">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg></span>
            <span>Collect Value Examples</span>
          </div>
          <p className="action-desc">
            Gather concrete examples of how your values show up in everyday work.
          </p>
          <button
            className="btn full"
            onClick={() => navigate('/values')}
          >
            Begin Collection →
          </button>
        </div>

        <div className="action-card">
          <div className="action-head">
            <span className="action-icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
              <g clip-path="url(#clip0_1_2646)">
                <path d="M2.66669 4.66675V4.00008C2.66669 3.64646 2.80716 3.30732 3.05721 3.05727C3.30726 2.80722 3.6464 2.66675 4.00002 2.66675H5.33335" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M2.66669 11.3333V11.9999C2.66669 12.3535 2.80716 12.6927 3.05721 12.9427C3.30726 13.1928 3.6464 13.3333 4.00002 13.3333H5.33335" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M10.6667 2.66675H12C12.3536 2.66675 12.6928 2.80722 12.9428 3.05727C13.1929 3.30732 13.3334 3.64646 13.3334 4.00008V4.66675" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M10.6667 13.3333H12C12.3536 13.3333 12.6928 13.1928 12.9428 12.9427C13.1929 12.6927 13.3334 12.3535 13.3334 11.9999V11.3333" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M3.33331 8H12.6666" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_1_2646">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg></span>
            <span>Complete Diagnostics</span>
          </div>
          <p className="action-desc">
            Identify your competency strength and identify growth opportunities.                  </p>
          <button
            className={`btn full ${!step2Unlocked ? 'disabled' : ''}`}
            onClick={() => step2Unlocked && navigate('/diagnostic')}
            disabled={!step2Unlocked}
          >
            Start Assessment →
          </button>
        </div>

        <div className="action-card">
          <div className="action-head">
            <span className="action-icon"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
              <g clip-path="url(#clip0_1_2655)">
                <path d="M2 8.66667C2 8.48986 2.07024 8.32029 2.19526 8.19526C2.32029 8.07024 2.48986 8 2.66667 8H5.33333C5.51014 8 5.67971 8.07024 5.80474 8.19526C5.92976 8.32029 6 8.48986 6 8.66667V12.6667C6 12.8435 5.92976 13.013 5.80474 13.1381C5.67971 13.2631 5.51014 13.3333 5.33333 13.3333H2.66667C2.48986 13.3333 2.32029 13.2631 2.19526 13.1381C2.07024 13.013 2 12.8435 2 12.6667V8.66667Z" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M10 5.99992C10 5.82311 10.0702 5.65354 10.1953 5.52851C10.3203 5.40349 10.4899 5.33325 10.6667 5.33325H13.3333C13.5101 5.33325 13.6797 5.40349 13.8047 5.52851C13.9298 5.65354 14 5.82311 14 5.99992V12.6666C14 12.8434 13.9298 13.013 13.8047 13.138C13.6797 13.263 13.5101 13.3333 13.3333 13.3333H10.6667C10.4899 13.3333 10.3203 13.263 10.1953 13.138C10.0702 13.013 10 12.8434 10 12.6666V5.99992Z" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M6 3.33341C6 3.1566 6.07024 2.98703 6.19526 2.86201C6.32029 2.73699 6.48986 2.66675 6.66667 2.66675H9.33333C9.51014 2.66675 9.67971 2.73699 9.80474 2.86201C9.92976 2.98703 10 3.1566 10 3.33341V12.6667C10 12.8436 9.92976 13.0131 9.80474 13.1382C9.67971 13.2632 9.51014 13.3334 9.33333 13.3334H6.66667C6.48986 13.3334 6.32029 13.2632 6.19526 13.1382C6.07024 13.0131 6 12.8436 6 12.6667V3.33341Z" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M2.66663 13.3333H12" stroke="#2A987A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_1_2655">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg></span>
            <span>View Competency Rubrics</span>
          </div>
          <p className="action-desc">
            See your competency rubrics that show what “good” looks like at each level.
          </p>
          <button
            className={`btn full ${!step3Unlocked ? 'disabled' : ''}`}
            onClick={() => step3Unlocked && navigate('/competencies')}
            disabled={!step3Unlocked}
          >
            Explore Rubrics →
          </button>
        </div>
      </section>
    </>
  );
}

